/**
 * Decision Store — persistent knowledge graph for architectural decisions,
 * tech choices, bug root causes, and preferences.
 *
 * Stored in ~/.trace-mcp/decisions.db (separate from per-repo code DBs).
 * Each decision has temporal validity (valid_from / valid_until) and optional
 * linkage to code symbols/files, enabling code-aware memory queries.
 */

import fs from 'node:fs';
import path from 'node:path';
import Database from 'better-sqlite3';
import { logger } from '../logger.js';
import { restrictDbPerms } from '../shared/db-perms.js';
import type { AuditLogger } from './decision-audit-log.js';
import { MemoOperations } from './decision-store-memo-ops.js';
import { ClusterOperations } from './decision-store-cluster-ops.js';
import { SchedulerStateOperations } from './decision-store-scheduler-ops.js';
import { ConsolidationOperations } from './decision-store-consolidation-ops.js';
import { SessionOperations } from './decision-store-session-ops.js';
import { QueryOperations } from './decision-store-query-ops.js';
import { ReviewOperations, extractSignalsForReview } from './decision-store-review-ops.js';
import { MutationOperations } from './decision-store-mutation-ops.js';
import type { ConsolidationVerdict } from './decision-consolidator.js';

// Re-export for back-compat: the tuner + tests import extractSignalsForReview
// from this module. It now lives in decision-store-review-ops.ts.
export { extractSignalsForReview };

// ════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════

// Shared types live in `decision-types.ts` so helper modules (clusterer,
// confidence scorer, consolidator, tuner) can import them without closing
// a cycle back through this store. Re-export here to preserve the public
// API — external callers keep importing from `./decision-store.js`.
export type {
  DecisionType,
  DecisionRow,
  DecisionInput,
  DecisionQuery,
  DecisionTimelineEntry,
  ClusterRow,
  ClusterInput,
  ClusterQuery,
  ProjectMemoRow,
  SessionChunkRow,
  SessionChunkInput,
  SessionSearchResult,
  ReviewEventRow,
  SchedulerStateRow,
  StaleDecisionReport,
  PruneDecisionsResult,
} from './decision-types.js';
import type {
  DecisionType,
  DecisionRow,
  DecisionInput,
  DecisionQuery,
  DecisionTimelineEntry,
  ClusterRow,
  ClusterInput,
  ClusterQuery,
  ProjectMemoRow,
  SessionChunkRow,
  SessionChunkInput,
  SessionSearchResult,
  ReviewEventRow,
  SchedulerStateRow,
  StaleDecisionReport,
  PruneDecisionsResult,
} from './decision-types.js';

// DecisionQuery, DecisionTimelineEntry, session-chunk, review-event,
// scheduler-state, cluster + memo row/query shapes moved to
// `decision-types.ts` so the extracted persistence modules can import them
// without closing an import cycle back through this store. Re-exported (with
// the DecisionType block above) for public-API back-compat.

// ════════════════════════════════════════════════════════════════════════
// SCHEMA
// ════════════════════════════════════════════════════════════════════════

const DECISIONS_DDL = `
CREATE TABLE IF NOT EXISTS decisions (
    id              INTEGER PRIMARY KEY,
    title           TEXT NOT NULL,
    content         TEXT NOT NULL,
    type            TEXT NOT NULL,
    project_root    TEXT NOT NULL,
    service_name    TEXT,
    symbol_id       TEXT,
    file_path       TEXT,
    tags            TEXT,
    valid_from      TEXT NOT NULL,
    valid_until     TEXT,
    session_id      TEXT,
    source          TEXT NOT NULL DEFAULT 'manual',
    confidence      REAL NOT NULL DEFAULT 1.0,
    git_branch      TEXT,
    review_status   TEXT,
    created_at      TEXT NOT NULL,
    hit_count       INTEGER NOT NULL DEFAULT 0,
    last_hit_at     TEXT
);
CREATE INDEX IF NOT EXISTS idx_decisions_project ON decisions(project_root);
CREATE INDEX IF NOT EXISTS idx_decisions_service ON decisions(service_name);
CREATE INDEX IF NOT EXISTS idx_decisions_type ON decisions(type);
CREATE INDEX IF NOT EXISTS idx_decisions_symbol ON decisions(symbol_id);
CREATE INDEX IF NOT EXISTS idx_decisions_file ON decisions(file_path);
CREATE INDEX IF NOT EXISTS idx_decisions_valid ON decisions(valid_from, valid_until);
CREATE INDEX IF NOT EXISTS idx_decisions_branch ON decisions(git_branch);
CREATE INDEX IF NOT EXISTS idx_decisions_file_branch ON decisions(file_path, git_branch);
CREATE INDEX IF NOT EXISTS idx_decisions_review_status ON decisions(review_status);

-- FTS5 virtual table for full-text search over decisions
CREATE VIRTUAL TABLE IF NOT EXISTS decisions_fts USING fts5(
    title,
    content,
    tags,
    content=decisions,
    content_rowid=id,
    tokenize='porter unicode61'
);

-- Triggers to keep FTS in sync
CREATE TRIGGER IF NOT EXISTS decisions_ai AFTER INSERT ON decisions BEGIN
    INSERT INTO decisions_fts(rowid, title, content, tags)
    VALUES (new.id, new.title, new.content, new.tags);
END;

CREATE TRIGGER IF NOT EXISTS decisions_ad AFTER DELETE ON decisions BEGIN
    INSERT INTO decisions_fts(decisions_fts, rowid, title, content, tags)
    VALUES ('delete', old.id, old.title, old.content, old.tags);
END;

CREATE TRIGGER IF NOT EXISTS decisions_au AFTER UPDATE ON decisions BEGIN
    INSERT INTO decisions_fts(decisions_fts, rowid, title, content, tags)
    VALUES ('delete', old.id, old.title, old.content, old.tags);
    INSERT INTO decisions_fts(rowid, title, content, tags)
    VALUES (new.id, new.title, new.content, new.tags);
END;

-- Mined sessions tracking (prevent re-mining)
CREATE TABLE IF NOT EXISTS mined_sessions (
    session_path     TEXT PRIMARY KEY,
    mined_at         TEXT NOT NULL,
    decisions_found  INTEGER NOT NULL DEFAULT 0,
    -- Incremental cursor (P2.3): re-mining a previously-mined file resumes
    -- from cursor_offset instead of re-parsing the whole file. last_size +
    -- last_modified_ms detect rotation/truncation and let unchanged files
    -- skip parsing entirely. preMigrate ALTERs upgrade legacy DBs.
    cursor_offset    INTEGER NOT NULL DEFAULT 0,
    last_size        INTEGER NOT NULL DEFAULT 0,
    last_modified_ms INTEGER NOT NULL DEFAULT 0
);

-- Mined provider sessions tracking (P2.3 extension to providers like Hermes/Codex).
--
-- Why a sibling table rather than overloading mined_sessions:
--   - Provider sessions (Hermes) are SQLite-backed — byte offsets don't apply.
--     We track a message-id / timestamp cursor instead.
--   - The session "path" is a synthetic key like "hermes:<id>" or "codex:<id>",
--     not a filesystem path, so reusing the same PRIMARY KEY namespace would
--     conflate two semantically distinct things.
--   - Provider rotation/truncation semantics differ: SQLite rows can be deleted
--     in-place, not just appended like a JSONL file.
--
-- Cursor primitive: last_timestamp_ms (highest message timestamp consumed) +
-- last_message_id (string, for deterministic ordering tie-breaks). On a re-pass
-- we skip every message whose (ts, id) is ≤ the cursor. last_seen_size +
-- last_seen_modified_ms detect SessionHandle-level rotation (DB shrank → restart).
CREATE TABLE IF NOT EXISTS mined_provider_sessions (
    session_key          TEXT PRIMARY KEY,        -- e.g. "hermes:<id>" or "codex:<id>"
    mined_at             TEXT NOT NULL,
    decisions_found      INTEGER NOT NULL DEFAULT 0,
    last_timestamp_ms    INTEGER NOT NULL DEFAULT 0,
    last_message_id      TEXT NOT NULL DEFAULT '',
    last_seen_size       INTEGER NOT NULL DEFAULT 0,
    last_seen_modified_ms INTEGER NOT NULL DEFAULT 0
);

-- ════════════════════════════════════════════════════════════════
-- LLM EXTRACTION CACHE — avoid re-paying LLM tokens on re-mining
-- ════════════════════════════════════════════════════════════════
-- Caches raw extracted JSON for a (session_id, content_sha, model) triple.
-- The content_sha keys the cache on the privacy-stripped transcript so a
-- session that hasn't changed (and a model that hasn't swapped) returns the
-- cached extraction. Different models against the same content live as
-- separate rows so a model upgrade transparently re-extracts.
CREATE TABLE IF NOT EXISTS llm_extraction_cache (
    session_id      TEXT NOT NULL,
    content_sha     TEXT NOT NULL,
    extracted_json  TEXT NOT NULL,
    model           TEXT NOT NULL,
    created_at      TEXT NOT NULL,
    PRIMARY KEY (session_id, content_sha, model)
);

-- ════════════════════════════════════════════════════════════════
-- SESSION CONTENT CHUNKS — cross-session semantic search
-- ════════════════════════════════════════════════════════════════

CREATE TABLE IF NOT EXISTS session_chunks (
    id              INTEGER PRIMARY KEY,
    session_id      TEXT NOT NULL,
    project_root    TEXT NOT NULL,
    chunk_index     INTEGER NOT NULL,
    role            TEXT NOT NULL,
    content         TEXT NOT NULL,
    timestamp       TEXT NOT NULL,
    /** Comma-separated file paths referenced in this chunk */
    referenced_files TEXT,
    UNIQUE(session_id, chunk_index)
);
CREATE INDEX IF NOT EXISTS idx_chunks_project ON session_chunks(project_root);
CREATE INDEX IF NOT EXISTS idx_chunks_session ON session_chunks(session_id);

-- FTS5 for session content search
CREATE VIRTUAL TABLE IF NOT EXISTS session_chunks_fts USING fts5(
    content,
    content=session_chunks,
    content_rowid=id,
    tokenize='porter unicode61'
);

CREATE TRIGGER IF NOT EXISTS chunks_ai AFTER INSERT ON session_chunks BEGIN
    INSERT INTO session_chunks_fts(rowid, content)
    VALUES (new.id, new.content);
END;

CREATE TRIGGER IF NOT EXISTS chunks_ad AFTER DELETE ON session_chunks BEGIN
    INSERT INTO session_chunks_fts(session_chunks_fts, rowid, content)
    VALUES ('delete', old.id, old.content);
END;

-- ════════════════════════════════════════════════════════════════
-- DECISION CLUSTERS (P1.1) — L2 thematic layer over raw decisions
-- ════════════════════════════════════════════════════════════════
-- A cluster is a short noun-phrase label + 1-3 sentence summary that
-- groups topically-related decisions. Produced by the LLM clusterer in
-- src/memory/decision-clusterer.ts. Cluster ids are stable across
-- re-runs via title similarity (see titleSimilarity + the merge logic
-- in the build_decision_clusters tool handler).
--
-- decision_cluster_members is the join table (one row per decision in
-- a cluster). The FK to decisions has ON DELETE CASCADE so dropping a
-- decision automatically drops its membership rows.
CREATE TABLE IF NOT EXISTS decision_clusters (
    id              INTEGER PRIMARY KEY,
    project_root    TEXT NOT NULL,
    service_name    TEXT,
    title           TEXT NOT NULL,
    summary         TEXT NOT NULL,
    tags            TEXT,
    primary_type    TEXT,
    decision_count  INTEGER NOT NULL DEFAULT 0,
    created_at      TEXT NOT NULL,
    updated_at      INTEGER NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_clusters_project ON decision_clusters(project_root);
CREATE INDEX IF NOT EXISTS idx_clusters_service ON decision_clusters(service_name);
CREATE INDEX IF NOT EXISTS idx_clusters_updated ON decision_clusters(updated_at);

CREATE TABLE IF NOT EXISTS decision_cluster_members (
    cluster_id      INTEGER NOT NULL REFERENCES decision_clusters(id) ON DELETE CASCADE,
    decision_id     INTEGER NOT NULL REFERENCES decisions(id) ON DELETE CASCADE,
    PRIMARY KEY (cluster_id, decision_id)
);
CREATE INDEX IF NOT EXISTS idx_cluster_members_decision ON decision_cluster_members(decision_id);

-- FTS5 virtual table for full-text search over cluster titles + summaries
CREATE VIRTUAL TABLE IF NOT EXISTS decision_clusters_fts USING fts5(
    title,
    summary,
    tags,
    content=decision_clusters,
    content_rowid=id,
    tokenize='porter unicode61'
);

-- Triggers to keep cluster FTS in sync
CREATE TRIGGER IF NOT EXISTS decision_clusters_ai AFTER INSERT ON decision_clusters BEGIN
    INSERT INTO decision_clusters_fts(rowid, title, summary, tags)
    VALUES (new.id, new.title, new.summary, new.tags);
END;

CREATE TRIGGER IF NOT EXISTS decision_clusters_ad AFTER DELETE ON decision_clusters BEGIN
    INSERT INTO decision_clusters_fts(decision_clusters_fts, rowid, title, summary, tags)
    VALUES ('delete', old.id, old.title, old.summary, old.tags);
END;

CREATE TRIGGER IF NOT EXISTS decision_clusters_au AFTER UPDATE ON decision_clusters BEGIN
    INSERT INTO decision_clusters_fts(decision_clusters_fts, rowid, title, summary, tags)
    VALUES ('delete', old.id, old.title, old.summary, old.tags);
    INSERT INTO decision_clusters_fts(rowid, title, summary, tags)
    VALUES (new.id, new.title, new.summary, new.tags);
END;

-- ════════════════════════════════════════════════════════════════
-- PROJECT MEMOS (L3 orientation digest)
-- ════════════════════════════════════════════════════════════════
-- LLM-synthesised Markdown orientation digest for a project (or per
-- service). Only the LATEST row per (project_root, service_name) is
-- read by surfaces — old rows are retained for history (regenerate
-- inserts a new row with version+1).
CREATE TABLE IF NOT EXISTS project_memos (
    id                      INTEGER PRIMARY KEY AUTOINCREMENT,
    project_root            TEXT NOT NULL,
    service_name            TEXT,
    memo_md                 TEXT NOT NULL,
    version                 INTEGER NOT NULL DEFAULT 1,
    model                   TEXT,
    created_at              TEXT NOT NULL,
    updated_at              TEXT NOT NULL,
    last_decision_id        INTEGER,
    decisions_at_generation INTEGER NOT NULL DEFAULT 0,
    clusters_at_generation  INTEGER NOT NULL DEFAULT 0,
    estimated_tokens        INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS idx_project_memos_scope
    ON project_memos(project_root, service_name);

-- ════════════════════════════════════════════════════════════════
-- DECISION REVIEWS (P2.5) — confidence-weight learning corpus
-- ════════════════════════════════════════════════════════════════
-- Every approve/reject toggle on a decision drops one row here so the
-- weight tuner can re-fit the confidence scorer over actual reviewer
-- feedback. signals_at_decision is a JSON blob of the features the
-- scorer used at capture time (has_code_ref, content_length, tag_count,
-- type, has_service) — recorded at review time, not capture time, so the
-- review row keeps a self-contained training example even if the
-- decision body gets edited later. FK cascade keeps the table clean
-- when a decision is hard-deleted.
CREATE TABLE IF NOT EXISTS decision_reviews (
    id                       INTEGER PRIMARY KEY AUTOINCREMENT,
    decision_id              INTEGER NOT NULL REFERENCES decisions(id) ON DELETE CASCADE,
    action                   TEXT NOT NULL CHECK(action IN ('approve','reject')),
    signals_at_decision      TEXT NOT NULL,
    confidence_at_decision   REAL NOT NULL,
    reviewed_at              TEXT NOT NULL,
    reviewer                 TEXT
);
CREATE INDEX IF NOT EXISTS idx_decision_reviews_action ON decision_reviews(action);
CREATE INDEX IF NOT EXISTS idx_decision_reviews_decision ON decision_reviews(decision_id);

-- ════════════════════════════════════════════════════════════════
-- SCHEDULER STATE — durable per-project cooldown bookkeeping
-- ════════════════════════════════════════════════════════════════
-- The background MemoryScheduler keeps per-project cooldown timestamps
-- (lastMineAt/lastClusterAt/lastMemoAt/lastTuneAt) plus the
-- consecutiveFailures back-off counter and a lastTuneEventCount baseline
-- in memory. Persisting them here lets a daemon restart skip stages that
-- were already run on the previous boot — avoiding a thundering herd of
-- expensive LLM-backed mine/cluster/memo/tune calls on the first tick.
CREATE TABLE IF NOT EXISTS scheduler_state (
    project_root            TEXT NOT NULL,
    last_mine_at            INTEGER,
    last_cluster_at         INTEGER,
    last_memo_at            INTEGER,
    last_tune_at            INTEGER,
    last_tune_event_count   INTEGER,
    consecutive_failures    INTEGER NOT NULL DEFAULT 0,
    updated_at              TEXT NOT NULL,
    PRIMARY KEY (project_root)
);
`;

// ════════════════════════════════════════════════════════════════════════
// STORE
// ════════════════════════════════════════════════════════════════════════

export class DecisionStore {
  public readonly db: Database.Database;
  /**
   * Optional day-bucketed JSONL audit logger. When set, every successful
   * add/update/invalidate is mirrored to the configured directory. Audit
   * writes are best-effort — failures never bubble out of the mutation
   * methods.
   */
  private auditLogger: AuditLogger | null;
  /**
   * Bounded retention for `project_memos`: keep at most N rows per
   * (project_root, service_name) scope. The retention prune runs in the
   * same transaction as the INSERT inside `saveProjectMemo`, so the table
   * can never exceed `historyLimit * scopes` at rest. Defaults to 10 — the
   * historical UI default for `listProjectMemos`.
   */
  private memoHistoryLimit: number;
  /** Project-memo persistence, extracted from this class (god-class decomposition). */
  private memoOps: MemoOperations;
  /** Decision-cluster persistence, extracted from this class (god-class decomposition). */
  private clusterOps: ClusterOperations;
  /** Scheduler-state persistence, extracted from this class (god-class decomposition). */
  private schedulerOps: SchedulerStateOperations;
  /** Semantic-dedup / consolidation logic, extracted from this class (god-class decomposition). */
  private consolidationOps: ConsolidationOperations;
  /** Session-tracking persistence (mining cursors, LLM cache, session chunks), extracted from this class. */
  private sessionOps: SessionOperations;
  /** Decision read-path (query / heat / timeline / stats), extracted from this class. */
  private queryOps: QueryOperations;
  /** Memoir-style review-queue persistence, extracted from this class. */
  private reviewOps: ReviewOperations;
  /** Decision write-path (CRUD + supersession), extracted from this class. */
  private mutationOps: MutationOperations;

  constructor(
    dbPath: string,
    opts?: {
      readonly?: boolean;
      auditLogger?: AuditLogger | null;
      memoHistoryLimit?: number;
    },
  ) {
    this.db = new Database(dbPath, { readonly: opts?.readonly ?? false });
    this.auditLogger = opts?.auditLogger ?? null;
    this.memoHistoryLimit = Math.max(1, opts?.memoHistoryLimit ?? 10);
    this.mutationOps = new MutationOperations(this.db, this.auditLogger);
    this.memoOps = new MemoOperations(this.db, this.memoHistoryLimit);
    this.clusterOps = new ClusterOperations(this.db);
    this.schedulerOps = new SchedulerStateOperations(this.db);
    this.consolidationOps = new ConsolidationOperations(this.db, {
      getDecision: (id) => this.getDecision(id),
      updateDecision: (id, patch) => {
        this.updateDecision(id, patch);
      },
      invalidateDecision: (id) => {
        this.invalidateDecision(id);
      },
    });
    this.sessionOps = new SessionOperations(this.db);
    this.queryOps = new QueryOperations(this.db, {
      getDecision: (id) => this.getDecision(id),
    });
    this.reviewOps = new ReviewOperations(this.db, {
      getDecision: (id) => this.getDecision(id),
    });
    if (opts?.readonly) {
      this.db.pragma('busy_timeout = 5000');
      logger.debug({ dbPath, readonly: true }, 'Decision store opened (readonly)');
    } else {
      restrictDbPerms(dbPath);
      this.db.pragma('journal_mode = WAL');
      // Bound WAL/journal growth: long-running daemons accumulating decisions
      // would otherwise grow `*-wal` unbounded between checkpoints. 100 MiB is
      // enough headroom for normal write bursts but caps worst-case disk use.
      this.db.pragma(`journal_size_limit = ${100 * 1024 * 1024}`);
      this.db.pragma('foreign_keys = ON');
      this.db.pragma('busy_timeout = 5000');
      this.preMigrate();
      this.db.exec(DECISIONS_DDL);
      this.migrate();
      this.scheduleWalCheckpoint();
      logger.debug({ dbPath }, 'Decision store initialized');
    }
  }

  private checkpointTimer: NodeJS.Timeout | null = null;

  /**
   * Periodic `wal_checkpoint(TRUNCATE)` keeps `*-wal` from growing forever in
   * a long-running daemon. SQLite auto-checkpoints on the writer thread, but
   * only when the WAL crosses 1000 frames — heavy read traffic with light
   * writes can stall a checkpoint indefinitely. TRUNCATE truncates the WAL
   * back to zero after a successful checkpoint.
   */
  private scheduleWalCheckpoint(): void {
    if (this.checkpointTimer !== null) return;
    const tick = () => {
      try {
        this.db.pragma('wal_checkpoint(TRUNCATE)');
      } catch (err) {
        // Non-fatal: another writer holds the lock, or DB just closed.
        logger.debug?.({ err: (err as Error).message }, 'wal_checkpoint failed');
      }
    };
    // 5 minutes — short enough to keep WAL bounded, long enough to amortise
    // checkpoint cost across many writes.
    this.checkpointTimer = setInterval(tick, 5 * 60 * 1000);
    // Don't pin the event loop on the checkpoint timer.
    this.checkpointTimer.unref?.();
  }

  private cancelWalCheckpoint(): void {
    if (this.checkpointTimer === null) return;
    clearInterval(this.checkpointTimer);
    this.checkpointTimer = null;
  }

  /**
   * Fix legacy schemas BEFORE DDL runs — prevents crashes when
   * CREATE INDEX references columns that don't exist in old tables.
   */
  private preMigrate(): void {
    const hasTable = this.db
      .prepare("SELECT name FROM sqlite_master WHERE type='table' AND name='decisions'")
      .get();
    if (hasTable) {
      const cols = (this.db.pragma('table_info(decisions)') as Array<{ name: string }>).map(
        (c) => c.name,
      );
      if (!cols.includes('service_name')) {
        this.db.exec('ALTER TABLE decisions ADD COLUMN service_name TEXT');
        logger.info('Pre-migration: added service_name column to decisions table');
      }
      // Branch-aware decision memory: existing rows backfill to NULL
      // (= branch-agnostic). Idempotent: ALTER TABLE only runs when column missing.
      if (!cols.includes('git_branch')) {
        this.db.exec('ALTER TABLE decisions ADD COLUMN git_branch TEXT');
        logger.info('Pre-migration: added git_branch column to decisions table');
      }
      // Memoir-style review queue: existing rows backfill to NULL
      // (= auto-approved / legacy, visible by default). Idempotent: ALTER TABLE
      // only runs when the column is missing — SQLite has no native
      // "ADD COLUMN IF NOT EXISTS", so we probe via PRAGMA table_info first.
      if (!cols.includes('review_status')) {
        this.db.exec('ALTER TABLE decisions ADD COLUMN review_status TEXT');
        logger.info('Pre-migration: added review_status column to decisions table');
      }
      // Heat / time-decay scoring (P2.1): track recall frequency and recency
      // so frequently-used decisions surface higher and stale ones fade.
      // Idempotent: ALTER TABLE only runs when the column is missing —
      // SQLite has no native "ADD COLUMN IF NOT EXISTS".
      if (!cols.includes('hit_count')) {
        this.db.exec('ALTER TABLE decisions ADD COLUMN hit_count INTEGER NOT NULL DEFAULT 0');
        logger.info('Pre-migration: added hit_count column to decisions table');
      }
      if (!cols.includes('last_hit_at')) {
        this.db.exec('ALTER TABLE decisions ADD COLUMN last_hit_at TEXT');
        logger.info('Pre-migration: added last_hit_at column to decisions table');
      }
    }

    // Incremental session-mining cursor (P2.3): augment the binary
    // mined/unmined `mined_sessions` row with a byte-offset cursor so a
    // long-running session file (Claude Code appends continuously) can be
    // re-mined for only the appended portion. Mirrors the existing
    // PRAGMA-table_info probe pattern: ALTER TABLE only runs when the
    // column is missing — SQLite has no native ADD COLUMN IF NOT EXISTS.
    const hasMinedSessions = this.db
      .prepare("SELECT name FROM sqlite_master WHERE type='table' AND name='mined_sessions'")
      .get();
    if (hasMinedSessions) {
      const minedCols = (
        this.db.pragma('table_info(mined_sessions)') as Array<{ name: string }>
      ).map((c) => c.name);
      if (!minedCols.includes('cursor_offset')) {
        this.db.exec(
          'ALTER TABLE mined_sessions ADD COLUMN cursor_offset INTEGER NOT NULL DEFAULT 0',
        );
        logger.info('Pre-migration: added cursor_offset column to mined_sessions table');
      }
      if (!minedCols.includes('last_size')) {
        this.db.exec('ALTER TABLE mined_sessions ADD COLUMN last_size INTEGER NOT NULL DEFAULT 0');
        logger.info('Pre-migration: added last_size column to mined_sessions table');
      }
      if (!minedCols.includes('last_modified_ms')) {
        this.db.exec(
          'ALTER TABLE mined_sessions ADD COLUMN last_modified_ms INTEGER NOT NULL DEFAULT 0',
        );
        logger.info('Pre-migration: added last_modified_ms column to mined_sessions table');
      }
    }
  }

  private migrate(): void {
    // (service_name migration now handled in preMigrate)
    // v2: add updated_at column (Unix ms) for incremental refresh.
    const hasUpdatedAt = (this.db.pragma('table_info(decisions)') as Array<{ name: string }>).some(
      (c) => c.name === 'updated_at',
    );
    if (!hasUpdatedAt) {
      this.db.exec(`
        ALTER TABLE decisions ADD COLUMN updated_at INTEGER;
        UPDATE decisions SET updated_at = strftime('%s','now')*1000 WHERE updated_at IS NULL;
      `);
      logger.info('Migration: added updated_at column to decisions table');
    }
  }

  /**
   * Update mutable fields on an existing decision.
   * Returns the updated row, or undefined when the id does not exist.
   */
  updateDecision(
    id: number,
    fields: Partial<
      Omit<
        Pick<
          DecisionRow,
          | 'title'
          | 'content'
          | 'type'
          | 'symbol_id'
          | 'file_path'
          | 'tags'
          | 'source'
          | 'confidence'
        >,
        'tags'
      > & {
        /** Either a string[] (will be JSON-stringified) or a raw JSON string. */
        tags?: string[] | string | null;
      }
    >,
  ): DecisionRow | undefined {
    return this.mutationOps.updateDecision(id, fields);
  }

  close(): void {
    this.cancelWalCheckpoint();
    // Final checkpoint before close so the next opener sees an empty WAL.
    try {
      if (this.db.open) this.db.pragma('wal_checkpoint(TRUNCATE)');
    } catch {
      /* best-effort */
    }
    this.db.close();
  }

  // ── CRUD ───────────────────────────────────────────────────────────

  /**
   * Insert a decision.
   *
   * When `opts.supersede` is true, any active decision sharing the SAME
   * state key (project_root + type + code anchor) is auto-invalidated with
   * `valid_until = now` before the new row lands — "state-key supersession"
   * (Task 11). The conflict heuristic is deliberately conservative:
   *   - same `project_root`
   *   - same `type`
   *   - same code anchor: `symbol_id` when the new row has one (file-only rows
   *     never collide with symbol-anchored rows), else `file_path`.
   * Rows with no anchor at all never supersede anything (no state key).
   *
   * For the caller-facing variant that also returns the superseded ids, use
   * `addDecisionWithSupersession`.
   */
  addDecision(input: DecisionInput, opts?: { supersede?: boolean }): DecisionRow {
    return this.mutationOps.addDecision(input, opts);
  }

  addDecisions(inputs: DecisionInput[]): number {
    return this.mutationOps.addDecisions(inputs);
  }

  /**
   * Insert a decision with auto-supersession, returning both the new row and
   * the ids of any active decisions that were invalidated as a result. Thin
   * wrapper over `addDecision(input, { supersede: true })` that captures the
   * superseded ids for the caller's response. See `addDecision` for the
   * conflict heuristic.
   */
  addDecisionWithSupersession(input: DecisionInput): {
    decision: DecisionRow;
    superseded: number[];
  } {
    return this.mutationOps.addDecisionWithSupersession(input);
  }

  getDecision(id: number): DecisionRow | undefined {
    return this.mutationOps.getDecision(id);
  }

  /**
   * Memoir-style review queue actions: stamp a decision as approved or
   * rejected (or back to pending). Returns true when a row was actually
   * updated. Backing for the `approve_decision` / `reject_decision` MCP
   * tools and the `/api/projects/decisions/:id/review` HTTP endpoint.
   */
  setReviewStatus(
    id: number,
    status: 'pending' | 'approved' | 'rejected',
    opts: { reviewer?: string | null } = {},
  ): boolean {
    return this.reviewOps.setReviewStatus(id, status, opts);
  }

  /**
   * Stream review events (approve/reject toggles) for the weight-tuner. When
   * `projectRoot` is set, results are filtered to reviews whose underlying
   * decision belongs to that project. Returns rows with signals already
   * parsed back into a structured object.
   */
  listReviewEvents(opts: { project_root?: string; limit?: number } = {}): Array<{
    id: number;
    decision_id: number;
    action: 'approve' | 'reject';
    signals: {
      has_code_ref: boolean;
      content_length: number;
      tag_count: number;
      type: string;
      has_service: boolean;
    };
    confidence_at_decision: number;
    reviewed_at: string;
    reviewer: string | null;
  }> {
    return this.reviewOps.listReviewEvents(opts);
  }

  /**
   * Count of rows currently in the review queue (`review_status = 'pending'`).
   * Used by the Memory Explorer Review tab badge and the wake-up surface.
   */
  countPendingReviews(projectRoot?: string): number {
    return this.reviewOps.countPendingReviews(projectRoot);
  }

  invalidateDecision(id: number, validUntil?: string): boolean {
    return this.mutationOps.invalidateDecision(id, validUntil);
  }

  deleteDecision(id: number): boolean {
    return this.mutationOps.deleteDecision(id);
  }

  // ── QUERY ──────────────────────────────────────────────────────────

  queryDecisions(query: DecisionQuery): DecisionRow[] {
    return this.queryOps.queryDecisions(query);
  }

  // ── HEAT / TIME-DECAY ─────────────────────────────────────────────────
  //
  // Heat tracks how often + how recently each decision is recalled by a
  // read-side surface (query_decisions, get_wake_up, get_decision_timeline).
  // `recordHits` is the write path; `getHeat`/`getHottest` are the read path.

  /**
   * Record one or more recall hits. Increments `hit_count` and stamps
   * `last_hit_at` to now() for every existing id in a single transaction.
   * Missing ids are silently ignored — callers don't care, and the
   * UPDATE's `WHERE id = ?` natively no-ops. Safe to call fire-and-forget.
   */
  recordHits(decisionIds: number[]): void {
    this.queryOps.recordHits(decisionIds);
  }

  /**
   * Compute the heat score for a single decision. Returns 0 when the
   * decision does not exist, so callers can treat this as a safe accessor.
   */
  getHeat(id: number, params?: { halfLifeDays?: number; freshnessDays?: number }): number {
    return this.queryOps.getHeat(id, params);
  }

  /**
   * Return the hottest N decisions (highest heat first). Filters mirror the
   * common query knobs: project_root, service_name, git_branch. Only active
   * (non-invalidated) and visible (NULL/approved) rows are considered.
   */
  getHottest(opts: {
    project_root?: string;
    service_name?: string;
    git_branch?: string;
    limit?: number;
    halfLifeDays?: number;
    freshnessDays?: number;
  }): DecisionRow[] {
    return this.queryOps.getHottest(opts);
  }

  // ── TIMELINE ───────────────────────────────────────────────────────

  getTimeline(opts: {
    project_root?: string;
    symbol_id?: string;
    file_path?: string;
    limit?: number;
  }): DecisionTimelineEntry[] {
    return this.queryOps.getTimeline(opts);
  }

  // ── STATS ──────────────────────────────────────────────────────────

  getStats(projectRoot?: string): {
    total: number;
    active: number;
    invalidated: number;
    by_type: Record<string, number>;
    by_source: Record<string, number>;
  } {
    return this.queryOps.getStats(projectRoot);
  }

  // ── MINED SESSIONS ────────────────────────────────────────────────

  isSessionMined(sessionPath: string): boolean {
    return this.sessionOps.isSessionMined(sessionPath);
  }

  /**
   * @deprecated Use {@link updateSessionCursor} for incremental cursor-aware
   * mining. Retained for back-compat with callers that haven't migrated.
   */
  markSessionMined(sessionPath: string, decisionsFound: number): void {
    this.sessionOps.markSessionMined(sessionPath, decisionsFound);
  }

  getMinedSessionCount(): number {
    return this.sessionOps.getMinedSessionCount();
  }

  /** Scoped variant of {@link getMinedSessionCount} — counts only `paths` that are mined. */
  getMinedSessionCountForPaths(paths: string[]): number {
    return this.sessionOps.getMinedSessionCountForPaths(paths);
  }

  /**
   * Get the next-read cursor for a previously-mined session.
   *
   * Semantics:
   *  - Row missing → `{ cursor: 0, reason: 'unmined' }`
   *  - File shrank → `{ cursor: 0, reason: 'restart_shrunk' }`
   *  - File unchanged (same size + same mtime) → `null` (caller should skip)
   *  - File grew → `{ cursor: cursor_offset, reason: 'incremental' }`
   */
  getSessionCursor(
    sessionPath: string,
    currentSize: number,
    currentModifiedMs?: number,
  ): { cursor: number; reason: 'unmined' | 'restart_shrunk' | 'incremental' } | null {
    return this.sessionOps.getSessionCursor(sessionPath, currentSize, currentModifiedMs);
  }

  /**
   * Atomically update the cursor after a successful mining pass.
   * Idempotent: re-running with identical inputs is a no-op write.
   */
  updateSessionCursor(opts: {
    sessionPath: string;
    cursor: number;
    size: number;
    modifiedMs: number;
    decisionsFound: number;
  }): void {
    this.sessionOps.updateSessionCursor(opts);
  }

  // ── MINED PROVIDER SESSIONS (Hermes / Codex incremental cursor) ───

  /** Marker returned by {@link getProviderSessionCursor}. `null` means "no
   *  changes since last pass — skip without iterating messages". */
  getProviderSessionCursor(
    sessionKey: string,
    currentSeenSize?: number,
    currentSeenModifiedMs?: number,
  ): {
    lastTimestampMs: number;
    lastMessageId: string;
    reason: 'unmined' | 'restart_shrunk' | 'incremental';
  } | null {
    return this.sessionOps.getProviderSessionCursor(
      sessionKey,
      currentSeenSize,
      currentSeenModifiedMs,
    );
  }

  /** Atomically update the provider-session cursor after a successful pass.
   *  Idempotent: identical inputs are a no-op write. */
  updateProviderSessionCursor(opts: {
    sessionKey: string;
    lastTimestampMs: number;
    lastMessageId: string;
    seenSize: number;
    seenModifiedMs: number;
    decisionsFound: number;
  }): void {
    this.sessionOps.updateProviderSessionCursor(opts);
  }

  // ── LLM EXTRACTION CACHE ──────────────────────────────────────────

  getCachedLlmExtraction(sessionId: string, contentSha: string, model: string): string | null {
    return this.sessionOps.getCachedLlmExtraction(sessionId, contentSha, model);
  }

  putCachedLlmExtraction(
    sessionId: string,
    contentSha: string,
    model: string,
    extractedJson: string,
  ): void {
    this.sessionOps.putCachedLlmExtraction(sessionId, contentSha, model, extractedJson);
  }

  // ── CODE-AWARE QUERIES ────────────────────────────────────────────

  /** Get all decisions linked to a specific symbol */
  getDecisionsForSymbol(symbolId: string, activeOnly = true): DecisionRow[] {
    const filter = activeOnly ? ' AND valid_until IS NULL' : '';
    return this.db
      .prepare(`SELECT * FROM decisions WHERE symbol_id = ?${filter} ORDER BY valid_from DESC`)
      .all(symbolId) as DecisionRow[];
  }

  /** Get all decisions linked to a specific file */
  getDecisionsForFile(filePath: string, activeOnly = true): DecisionRow[] {
    const filter = activeOnly ? ' AND valid_until IS NULL' : '';
    return this.db
      .prepare(`SELECT * FROM decisions WHERE file_path = ?${filter} ORDER BY valid_from DESC`)
      .all(filePath) as DecisionRow[];
  }

  /** Get decisions linked to any file matching a pattern (e.g., 'src/auth/%') */
  getDecisionsForPath(pathPattern: string, activeOnly = true): DecisionRow[] {
    const filter = activeOnly ? ' AND valid_until IS NULL' : '';
    return this.db
      .prepare(`SELECT * FROM decisions WHERE file_path LIKE ?${filter} ORDER BY valid_from DESC`)
      .all(pathPattern) as DecisionRow[];
  }

  /** Get all decisions for a specific subproject within a project */
  getDecisionsForService(
    serviceName: string,
    projectRoot?: string,
    activeOnly = true,
  ): DecisionRow[] {
    const filter = activeOnly ? ' AND valid_until IS NULL' : '';
    if (projectRoot) {
      return this.db
        .prepare(
          `SELECT * FROM decisions WHERE service_name = ? AND project_root = ?${filter} ORDER BY valid_from DESC`,
        )
        .all(serviceName, projectRoot) as DecisionRow[];
    }
    return this.db
      .prepare(`SELECT * FROM decisions WHERE service_name = ?${filter} ORDER BY valid_from DESC`)
      .all(serviceName) as DecisionRow[];
  }

  /** Get all distinct service names in a project */
  getServiceNames(projectRoot: string): string[] {
    const rows = this.db
      .prepare(
        'SELECT DISTINCT service_name FROM decisions WHERE project_root = ? AND service_name IS NOT NULL',
      )
      .all(projectRoot) as Array<{ service_name: string }>;
    return rows.map((r) => r.service_name);
  }

  // ── SESSION CHUNKS (cross-session content search) ─────────────────

  addSessionChunks(chunks: SessionChunkInput[]): number {
    return this.sessionOps.addSessionChunks(chunks);
  }

  isSessionIndexed(sessionId: string): boolean {
    return this.sessionOps.isSessionIndexed(sessionId);
  }

  searchSessions(
    query: string,
    opts: {
      project_root?: string;
      limit?: number;
    } = {},
  ): SessionSearchResult[] {
    return this.sessionOps.searchSessions(query, opts);
  }

  getSessionChunkCount(projectRoot?: string): number {
    return this.sessionOps.getSessionChunkCount(projectRoot);
  }

  getIndexedSessionIds(projectRoot?: string): string[] {
    return this.sessionOps.getIndexedSessionIds(projectRoot);
  }

  // ── DECISION CLUSTERS (P1.1) ─────────────────────────────────────────
  //
  // Clusters are an L2 thematic overlay produced by the LLM clusterer in
  // `src/memory/decision-clusterer.ts`. Each cluster carries a noun-phrase
  // title + summary + tag list + member decision ids. Cluster ids are
  // stable across `build_decision_clusters` re-runs via title similarity
  // (the tool handler merges freshly-computed clusters into existing rows
  // when their titles agree ≥0.8).

  /**
   * Insert a cluster + its membership rows in a single transaction.
   * `decision_count` is set from the input `decision_ids.length`.
   * Returns the inserted ClusterRow.
   */
  createCluster(input: ClusterInput): ClusterRow {
    return this.clusterOps.createCluster(input);
  }

  /**
   * Update an existing cluster's title/summary/tags/primary_type and
   * replace its membership list. Used when `build_decision_clusters`
   * merges a freshly-computed cluster into an existing row.
   */
  updateCluster(
    id: number,
    input: {
      title?: string;
      summary?: string;
      tags?: string[];
      primary_type?: DecisionType | null;
      decision_ids?: number[];
    },
  ): ClusterRow | undefined {
    return this.clusterOps.updateCluster(id, input);
  }

  /** Single-cluster lookup by id. */
  getCluster(id: number): ClusterRow | undefined {
    return this.clusterOps.getCluster(id);
  }

  /**
   * List clusters with optional project/service/search filters.
   * Default ordering is by `decision_count DESC` (largest first) so the
   * wake-up "topics" surface lands the biggest topics on top.
   */
  listClusters(query: ClusterQuery = {}): ClusterRow[] {
    return this.clusterOps.listClusters(query);
  }

  /**
   * Get the member decisions of a cluster. Respects active-only by default
   * (matches `query_decisions` defaults). `include_invalidated` returns
   * the full historical membership.
   */
  getClusterDecisions(
    clusterId: number,
    opts: { limit?: number; include_invalidated?: boolean } = {},
  ): DecisionRow[] {
    return this.clusterOps.getClusterDecisions(clusterId, opts);
  }

  /**
   * Find all clusters a decision belongs to. Used by surfaces that want
   * to annotate a decision with its parent topic(s).
   */
  findClustersForDecision(decisionId: number): ClusterRow[] {
    return this.clusterOps.findClustersForDecision(decisionId);
  }

  /**
   * Delete a cluster. The `ON DELETE CASCADE` on
   * `decision_cluster_members.cluster_id` drops the membership rows
   * automatically. Returns true when a row was removed.
   */
  deleteCluster(id: number): boolean {
    return this.clusterOps.deleteCluster(id);
  }

  /**
   * Drop every cluster (and cascade-drop their membership rows) for a
   * scope. Used by `build_decision_clusters` when `force=true` to start
   * from a clean slate. Returns the number of cluster rows removed.
   */
  deleteClustersForScope(opts: { project_root?: string; service_name?: string }): number {
    return this.clusterOps.deleteClustersForScope(opts);
  }

  /** Count clusters in a scope (used by tests + stats). */
  countClusters(opts: { project_root?: string; service_name?: string } = {}): number {
    return this.clusterOps.countClusters(opts);
  }

  // ── PROJECT MEMOS (L3 orientation digest) ───────────────────────────
  //
  // Project memos are LLM-synthesised Markdown orientation digests over the
  // decision store. Each regeneration inserts a NEW row (version+1) rather
  // than overwriting — old memos are retained for history. Read paths only
  // ever surface the LATEST row per (project_root, service_name).

  /**
   * Insert a new project memo row. Computes `version` as `prev.version + 1`
   * when an earlier memo exists for the same (project_root, service_name)
   * scope, otherwise 1. Returns the new {id, version}.
   */
  saveProjectMemo(input: {
    project_root: string;
    service_name?: string;
    memo_md: string;
    model?: string;
    last_decision_id?: number;
    decisions_at_generation: number;
    clusters_at_generation: number;
    estimated_tokens: number;
  }): { id: number; version: number } {
    return this.memoOps.saveProjectMemo(input);
  }

  /**
   * Return the most-recent memo for a (project_root, service_name) scope, or
   * undefined when no memo has been generated. `service_name` semantics
   * mirror the decisions/clusters surfaces — omit for the project-wide memo,
   * supply a name for the per-service one.
   */
  getLatestProjectMemo(opts: {
    project_root: string;
    service_name?: string;
  }): ProjectMemoRow | undefined {
    return this.memoOps.getLatestProjectMemo(opts);
  }

  /**
   * List historical memos in a scope (most-recent first). Default limit 10.
   * Used by `get_project_memo` when `include_history=true`.
   */
  listProjectMemos(opts: {
    project_root: string;
    service_name?: string;
    limit?: number;
  }): ProjectMemoRow[] {
    return this.memoOps.listProjectMemos(opts);
  }

  /**
   * Count of decisions whose id > the latest memo's `last_decision_id`,
   * scoped to the same project (+ optional service). Drives the auto-
   * regen threshold in `regenerate_project_memo`. When no prior memo
   * exists, returns the total active decision count in scope.
   */
  countDecisionsSinceLastMemo(opts: { project_root: string; service_name?: string }): number {
    return this.memoOps.countDecisionsSinceLastMemo(opts);
  }

  // ── SCHEDULER STATE (durable per-project cooldown bookkeeping) ────
  //
  // The background MemoryScheduler keeps lastMineAt / lastClusterAt /
  // lastMemoAt / lastTuneAt / consecutiveFailures in memory. Persisting
  // them lets a daemon restart skip stages that already ran on the
  // previous boot — avoiding a thundering herd of LLM-backed mine /
  // cluster / memo / tune calls on the first tick.

  /** Fetch the persisted scheduler state for a project, or undefined. */
  getSchedulerState(projectRoot: string): SchedulerStateRow | undefined {
    return this.schedulerOps.getSchedulerState(projectRoot);
  }

  /**
   * Upsert per-project scheduler state. Merge semantics:
   *   - `undefined` fields PRESERVE the existing column value (no overwrite).
   *   - `null` fields EXPLICITLY clear the column.
   *   - `consecutive_failures` defaults to existing value on update,
   *     or 0 on insert.
   *   - `updated_at` is always stamped to ISO `now()`.
   *
   * Runs inside a transaction so the read-then-write merge is atomic.
   */
  upsertSchedulerState(input: {
    project_root: string;
    last_mine_at?: number | null;
    last_cluster_at?: number | null;
    last_memo_at?: number | null;
    last_tune_at?: number | null;
    last_tune_event_count?: number | null;
    consecutive_failures?: number;
  }): void {
    this.schedulerOps.upsertSchedulerState(input);
  }

  // ── SEMANTIC DEDUP / CONSOLIDATION (P2.2) ─────────────────────────
  //
  // Two surfaces backing the `consolidate_decisions` MCP tool:
  //   - `findSimilarDecisions` — top-K candidates for a subject via FTS5
  //     full-text match + title trigram similarity.
  //   - `applyConsolidationVerdict` — atomic application of one LLM
  //     verdict (merge / replace / invalidate / keep_separate).

  /**
   * Return up to `topK` candidates similar to the given subject, sorted by
   * title trigram similarity (descending). Combines:
   *   - FTS5 match on the subject's title words (OR-joined; quoted for
   *     safety against FTS operators in titles)
   *   - same-`type` filter when `same_type_only`
   *   - trigram Jaccard floor (`min_title_similarity`)
   *   - excludes the subject itself, optionally invalidated rows
   *
   * Returns [] when the subject has no FTS-extractable title words and
   * no candidate set can be produced cheaply.
   */
  findSimilarDecisions(opts: {
    subject_id: number;
    topK?: number;
    min_title_similarity?: number;
    same_type_only?: boolean;
    project_root?: string;
    active_only?: boolean;
  }): DecisionRow[] {
    return this.consolidationOps.findSimilarDecisions(opts);
  }

  /**
   * Atomically apply one consolidation verdict. Returns whether any write
   * happened and which row ids were touched (useful for the MCP response
   * envelope and audit logs).
   *
   * Verdict semantics:
   *   - `keep_separate`        → no-op, applied=false
   *   - `merge_into_existing`  → update existing.content (concat unless
   *     `merged_content` is provided), union tags, invalidate subject
   *   - `replace_existing`     → invalidate existing, subject untouched
   *   - `invalidate_existing`  → invalidate existing only
   *
   * Runs in a single transaction so a mid-flight failure rolls back
   * cleanly. Returns `applied:false` when either row is missing or already
   * invalidated (defensive — the LLM may pick a row that was just
   * invalidated by a prior verdict in the same batch).
   */
  applyConsolidationVerdict(opts: {
    subject_id: number;
    verdict: ConsolidationVerdict;
    /** Optional: caller-supplied merged content. If absent, plain concat. */
    merged_content?: string;
  }): { applied: boolean; affected_ids: number[] } {
    return this.consolidationOps.applyConsolidationVerdict(opts);
  }

  // ── STALE ROOT PRUNING / HYGIENE ───────────────────────────────────

  /** Find distinct project_root paths across all memory tables that no longer exist on disk. */
  findStaleRoots(): string[] {
    const tables = [
      'decisions',
      'session_chunks',
      'decision_clusters',
      'project_memos',
      'scheduler_state',
    ];
    const roots = new Set<string>();
    for (const table of tables) {
      try {
        const rows = this.db.prepare(`SELECT DISTINCT project_root FROM ${table}`).all() as Array<{
          project_root: string;
        }>;
        for (const r of rows) {
          if (r.project_root) roots.add(r.project_root);
        }
      } catch {
        /* table missing on legacy db */
      }
    }
    return [...roots].filter((r) => !fs.existsSync(r));
  }

  /** Scan for orphaned decisions, chunks, clusters, and memos belonging to missing project roots. */
  findStale(): StaleDecisionReport {
    const staleRoots = this.findStaleRoots();

    let staleDecisions: Array<{
      id: number;
      title: string;
      project_root: string;
      type: DecisionType;
    }> = [];
    let decisionsCount = 0;
    let chunksCount = 0;
    let clustersCount = 0;
    let memosCount = 0;
    let schedulerStatesCount = 0;
    let staleMinedSessionsCount = 0;

    if (staleRoots.length > 0) {
      const placeholders = staleRoots.map(() => '?').join(',');
      try {
        const dRows = this.db
          .prepare(
            `SELECT id, title, project_root, type FROM decisions WHERE project_root IN (${placeholders})`,
          )
          .all(...staleRoots) as Array<{
          id: number;
          title: string;
          project_root: string;
          type: DecisionType;
        }>;
        staleDecisions = dRows;
        decisionsCount = dRows.length;
      } catch {
        /* ignore */
      }

      try {
        const row = this.db
          .prepare(
            `SELECT COUNT(*) as c FROM session_chunks WHERE project_root IN (${placeholders})`,
          )
          .get(...staleRoots) as { c: number };
        chunksCount = row.c;
      } catch {
        /* ignore */
      }

      try {
        const row = this.db
          .prepare(
            `SELECT COUNT(*) as c FROM decision_clusters WHERE project_root IN (${placeholders})`,
          )
          .get(...staleRoots) as { c: number };
        clustersCount = row.c;
      } catch {
        /* ignore */
      }

      try {
        const row = this.db
          .prepare(
            `SELECT COUNT(*) as c FROM project_memos WHERE project_root IN (${placeholders})`,
          )
          .get(...staleRoots) as { c: number };
        memosCount = row.c;
      } catch {
        /* ignore */
      }

      try {
        const row = this.db
          .prepare(
            `SELECT COUNT(*) as c FROM scheduler_state WHERE project_root IN (${placeholders})`,
          )
          .get(...staleRoots) as { c: number };
        schedulerStatesCount = row.c;
      } catch {
        /* ignore */
      }
    }

    try {
      const minedRows = this.db.prepare('SELECT session_path FROM mined_sessions').all() as Array<{
        session_path: string;
      }>;
      staleMinedSessionsCount = minedRows.filter(
        (r) => r.session_path && path.isAbsolute(r.session_path) && !fs.existsSync(r.session_path),
      ).length;
    } catch {
      /* ignore */
    }

    return {
      staleRoots,
      decisionsCount,
      chunksCount,
      clustersCount,
      memosCount,
      schedulerStatesCount,
      staleMinedSessionsCount,
      staleDecisions,
    };
  }

  /**
   * Prune decisions and associated data for project roots that no longer exist on disk.
   * Runs in a single transaction.
   */
  pruneStale(opts?: {
    staleRoots?: string[];
    includeMinedSessions?: boolean;
  }): PruneDecisionsResult {
    const staleRoots = opts?.staleRoots ?? this.findStaleRoots();
    const result: PruneDecisionsResult = {
      staleRoots,
      decisions: 0,
      chunks: 0,
      clusters: 0,
      memos: 0,
      schedulerStates: 0,
      minedSessions: 0,
    };

    this.db.transaction(() => {
      if (staleRoots.length > 0) {
        const placeholders = staleRoots.map(() => '?').join(',');

        try {
          const res = this.db
            .prepare(`DELETE FROM decisions WHERE project_root IN (${placeholders})`)
            .run(...staleRoots);
          result.decisions = res.changes;
        } catch {
          /* ignore */
        }

        try {
          const res = this.db
            .prepare(`DELETE FROM session_chunks WHERE project_root IN (${placeholders})`)
            .run(...staleRoots);
          result.chunks = res.changes;
        } catch {
          /* ignore */
        }

        try {
          const res = this.db
            .prepare(`DELETE FROM decision_clusters WHERE project_root IN (${placeholders})`)
            .run(...staleRoots);
          result.clusters = res.changes;
        } catch {
          /* ignore */
        }

        try {
          const res = this.db
            .prepare(`DELETE FROM project_memos WHERE project_root IN (${placeholders})`)
            .run(...staleRoots);
          result.memos = res.changes;
        } catch {
          /* ignore */
        }

        try {
          const res = this.db
            .prepare(`DELETE FROM scheduler_state WHERE project_root IN (${placeholders})`)
            .run(...staleRoots);
          result.schedulerStates = res.changes;
        } catch {
          /* ignore */
        }
      }

      if (opts?.includeMinedSessions) {
        try {
          const minedRows = this.db
            .prepare('SELECT session_path FROM mined_sessions')
            .all() as Array<{ session_path: string }>;
          const staleMinedPaths = minedRows
            .map((r) => r.session_path)
            .filter((p) => p && path.isAbsolute(p) && !fs.existsSync(p));
          if (staleMinedPaths.length > 0) {
            const placeholders = staleMinedPaths.map(() => '?').join(',');
            const res = this.db
              .prepare(`DELETE FROM mined_sessions WHERE session_path IN (${placeholders})`)
              .run(...staleMinedPaths);
            result.minedSessions = res.changes;
          }
        } catch {
          /* ignore */
        }
      }
    })();

    return result;
  }
}
